import { Select, SelectProps, Spin } from 'antd';
import { Dispatch, useEffect, useRef, useState } from 'react';
import { useList } from '../../Hooks/useList';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

type LabelInValueType = {
  key?: string;
  value: string | number;
  label?: React.ReactNode;
  item?: any;
};

type Props = {
  queryKey: string;
  openNewModal?: Dispatch<any>;
  initialOptions?: any[];
  loading?: boolean;
  valueField?: string;
  outerFilters?: { [key: string]: any };
  baseURL?: string;
  searchField?: string;
  labelRender?: (option: LabelInValueType | { item: any }) => React.ReactNode;
  showSelectAllOptions?: boolean;
  singleMode?: boolean;
  customerId?: string | number;
  onProductAdded?: (product: any) => void;
} & SelectProps;

export const InfiniteSelect = ({
  queryKey,
  openNewModal,
  initialOptions,
  labelRender,
  loading,
  valueField,
  searchField = 'name',
  outerFilters,
  baseURL,
  showSelectAllOptions = false,
  singleMode = false,
  customerId,
  onProductAdded,
  onSelect,
  ...antSelectProps
}: Props) => {
  const { t } = useTranslation();
  const {
    filters: listFilters,
    setListFilters: setListFilters,
    data: listData,
    isLoading: listLoading,
  } = useList(queryKey, {
    baseURL,
    method: 'GET',
    initialFilters: {
      filters: { search: '', ...outerFilters },
      paging: { page: 1, pageSize: 20 },
      sorting: {},
    },
    disableErrorHandling: true, // Disable error messages for search - errors are expected during typing
  });
  const [totalList, setTotalList] = useState<any[]>([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentPage, setCurrentPage] = useState(1);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [totalCount, setTotalCount] = useState(0);
  const isFirstRender = useRef(true);
  const lastScrollTop = useRef(0);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Type the listData properly
  const typedListData = listData as
    | {
        data: any[] | { data: any[]; current_page: number; total: number };
        count?: number;
        current_page?: number;
        total?: number;
        last_page?: number;
        per_page?: number;
        from?: number;
        to?: number;
      }
    | {
        count?: number;
        current_page?: number;
        total?: number;
        last_page?: number;
        per_page?: number;
        from?: number;
        to?: number;
      }
    | undefined;

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isFirstRender.current) {
      setTotalList(() => []);
      setHasMoreData(true);
      setIsLoadingMore(false);
      setCurrentPage(1);
      setTotalCount(0);
      // Reset to page 1 when filters change
      setListFilters('paging.page', 1);
    } else {
      isFirstRender.current = false;
    }

    setListFilters('filters', {
      ...listFilters.filters,
      ...outerFilters,
    });
  }, [outerFilters]);

  useEffect(() => {
    if (!typedListData || !('data' in typedListData)) return;

    // Handle both direct data array and paginated response
    const dataArray = Array.isArray(typedListData.data)
      ? typedListData.data
      : typedListData.data?.data || [];
    const responseCurrentPage = typedListData.current_page || listFilters.paging.page;
    const responseTotalCount = typedListData.total || typedListData.count || 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const lastPage = typedListData.last_page || 0;

    // Update our tracking states
    setCurrentPage(responseCurrentPage);
    setTotalCount(responseTotalCount);

    // Reset list for first page, append for subsequent pages
    if (responseCurrentPage === 1) {
      setTotalList(dataArray);
      // For first page, assume there's more data if we got any data
      // We'll know for sure when we get an empty response
      const hasMore = dataArray.length > 0;
      setHasMoreData(hasMore);
    } else {
      setTotalList((prevList) => {
        const newList = [...prevList, ...dataArray];
        // Simple rule: if we got data, assume there might be more
        // Only stop when we get an empty response (dataArray.length === 0)
        const hasMore = dataArray.length > 0;
        setHasMoreData(hasMore);
        return newList;
      });
    }

    setIsLoadingMore(false);
  }, [typedListData && 'data' in typedListData ? typedListData.data : null, outerFilters]);

  // to handle the initial value that it's labels not fetched yet
  useEffect(() => {
    if (!initialOptions || !initialOptions.length) return;
    setTotalList((prevList) => {
      // Ensure we don't add duplicates
      const newList = [...prevList];
      initialOptions.forEach((option) => {
        const exists = newList.some(
          (item) =>
            (valueField ? item[valueField] : item?.id || item?._id) ===
            (valueField ? option[valueField] : option?.id || option?._id)
        );
        if (!exists) {
          newList.unshift(option); // Add to beginning
        }
      });
      return newList;
    });
  }, [initialOptions]);

  const getDisplayName = (item: any) => {
    const currentLang = localStorage.getItem('i18nextLng') || 'en';
    const displayName =
      currentLang === 'ar'
        ? item?.name_ar || item?.name?.name_ar || item?.name
        : item?.name_en || item?.name?.name_en || item?.name || item[searchField] || '-';
    return displayName;
  };

  // Ensure selected value is always in options (for singleMode)
  const generateOptions = () => {
    const optionsList = [...totalList];

    // For singleMode, ensure initialOptions are always included in the options
    if (singleMode && initialOptions && initialOptions.length > 0) {
      initialOptions.forEach((initialOption) => {
        const existsInList = optionsList.some(
          (item) =>
            (valueField ? item[valueField] : item?.id || item?._id) ===
            (valueField ? initialOption[valueField] : initialOption?.id || initialOption?._id)
        );
        if (!existsInList) {
          optionsList.unshift(initialOption); // Add to beginning
        }
      });
    }

    return _.uniqBy(
      optionsList?.map((item) => {
        const label = labelRender ? labelRender({ item }) : getDisplayName(item);
        return {
          label,
          value: valueField ? item[valueField] : item?.id || item?._id,
          item,
        };
      }),
      'value'
    );
  };

  return (
    <Select
      mode={singleMode ? undefined : 'multiple'}
      labelInValue={!singleMode}
      options={generateOptions()}
      filterOption={() => true}
      onSearch={(search) => {
        // Clear previous debounce timer
        if (searchDebounceRef.current) {
          clearTimeout(searchDebounceRef.current);
        }

        // Reset list immediately if search is cleared or too short
        if (search.length <= 2) {
          setTotalList(() => []);
          setHasMoreData(true);
          setIsLoadingMore(false);
          setCurrentPage(1);
          setTotalCount(0);
          setListFilters('paging.page', 1);
          setListFilters('filters.search', '');
          return;
        }

        // Debounce the search API call
        searchDebounceRef.current = setTimeout(() => {
          setTotalList(() => []);
          setHasMoreData(true);
          setIsLoadingMore(false);
          setCurrentPage(1);
          setTotalCount(0);
          setListFilters('paging.page', 1);
          setListFilters('filters.search', search);
        }, 300); // 300ms debounce delay
      }}
      onSelect={(value, option) => {
        setListFilters('filters.search', '');

        // If customerId is provided, call the callback with selected product
        if (singleMode && customerId && option?.item) {
          onProductAdded?.(option.item);
        }

        // Call parent's onSelect if provided
        onSelect?.(value, option);
      }}
      showSearch
      onPopupScroll={async (e: any) => {
        const { target } = e;
        const scrollTop = target.scrollTop;
        const offsetHeight = target.offsetHeight;
        const scrollHeight = target.scrollHeight;

        // Detect scroll down near bottom (within 30px for better detection)
        const isNearBottom = scrollTop + offsetHeight >= scrollHeight - 30;
        const isScrollingDown = scrollTop > lastScrollTop.current;

        lastScrollTop.current = scrollTop;

        // Load more data if:
        // 1. Near bottom of scroll area
        // 2. Scrolling downward
        // 3. Not currently loading
        // 4. Has more data to load
        // 5. Not already loading more
        if (
          isNearBottom &&
          isScrollingDown &&
          !listLoading &&
          !isLoadingMore &&
          hasMoreData &&
          totalList.length > 0
        ) {
          setIsLoadingMore(true);
          setListFilters('paging.page', listFilters.paging.page + 1);
        }
      }}
      loading={listLoading || loading}
      suffixIcon={listLoading || loading ? <Spin /> : undefined}
      notFoundContent={listLoading ? <Spin /> : null}
      allowClear
      dropdownRender={(menu) => (
        <>
          {showSelectAllOptions && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
              }}
            >
              {/*<Button type="link" onClick={handleSelectAll}>*/}
              {/*  Select All*/}
              {/*</Button>*/}
              {/*<Button type="link" onClick={handleDeselectAll}>*/}
              {/*  Deselect All*/}
              {/*</Button>*/}
            </div>
          )}
          {menu}
          {/* Show loading indicator when loading more pages */}
          {(isLoadingMore || (listLoading && !!totalList.length)) && (
            <div style={{ textAlign: 'center', padding: '8px' }}>
              <Spin size="small" />
              <span
                style={{
                  marginLeft: '8px',
                  fontSize: '12px',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {t('common.loading', 'Loading more...')}
              </span>
            </div>
          )}
          {/* Show "No more data" indicator when all data is loaded */}
          {!hasMoreData && totalList.length > 0 && !listLoading && !isLoadingMore && (
            <div
              style={{
                textAlign: 'center',
                padding: '8px',
                fontSize: '12px',
                color: 'var(--color-text-tertiary)',
              }}
            >
              {t('common.noMoreData', 'No more items')}
            </div>
          )}
          {openNewModal && (
            <div className="ant-select-item add-new" onClick={() => openNewModal({})}>
              {t('common.addNew', 'Add New')} {queryKey.split('/')[queryKey.split('/').length - 1]}
            </div>
          )}
        </>
      )}
      {...antSelectProps}
    />
  );
};
